import React from 'react';
import { Field, Toggle } from './shared';

export const QUESTION_TYPES = [
  { value: 'name', label: 'Nome' },
  { value: 'email', label: 'E-mail' },
  { value: 'phone', label: 'Telefone / WhatsApp' },
  { value: 'short_text', label: 'Texto curto' },
  { value: 'long_text', label: 'Texto longo' },
  { value: 'number', label: 'Número' },
  { value: 'single_choice', label: 'Escolha única' },
  { value: 'multi_choice', label: 'Múltipla escolha' },
  { value: 'yes_no', label: 'Sim / Não' },
  { value: 'notice', label: 'Aviso / consentimento' },
];

export const MAX_QUESTIONS = 20;
export const MAX_OPTIONS = 20;
const isChoice = (t) => t === 'single_choice' || t === 'multi_choice';

const STANDARD_MAPS = [
  { value: '', label: 'Não mapear' },
  { value: 'contact.name', label: 'Contato: nome' },
  { value: 'contact.email', label: 'Contato: e-mail' },
  { value: 'contact.phone', label: 'Contato: telefone' },
  { value: 'custom', label: 'Campo personalizado...' },
];

const mapsToSelectValue = (m) => (!m ? '' : m.startsWith('custom:') ? 'custom' : m);

export const nextQuestionId = (questions) => {
  const used = new Set(questions.map((q) => q.id));
  let n = questions.length + 1;
  while (used.has(`q${n}`)) n += 1;
  return `q${n}`;
};

function QuestionCard({ q, index, total, linked, onChange, onMove, onRemove }) {
  const set = (patch) => onChange({ ...q, ...patch });
  const setType = (type) => {
    const next = { ...q, type };
    if (isChoice(type) && !(next.options || []).length) next.options = ['Opção 1'];
    if (!isChoice(type)) delete next.options;
    onChange(next);
  };
  const options = q.options || [];

  return (
    <div className="card p-4 space-y-3 border border-slate-100">
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-xs font-bold text-slate-400 w-6 shrink-0">{index + 1}.</span>
          <input
            className="input flex-1 min-w-0" value={q.label || ''} maxLength={500}
            placeholder="Texto da pergunta" aria-label="Texto da pergunta"
            onChange={(e) => set({ label: e.target.value })}
          />
        </div>
        <div className="flex items-center gap-1 self-end sm:self-auto shrink-0">
        <button type="button" title="Subir" aria-label="Subir" disabled={index === 0} onClick={() => onMove(-1)}
          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30">
          <span className="material-symbols-outlined text-[18px]">arrow_upward</span>
        </button>
        <button type="button" title="Descer" aria-label="Descer" disabled={index === total - 1} onClick={() => onMove(1)}
          className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30">
          <span className="material-symbols-outlined text-[18px]">arrow_downward</span>
        </button>
        <button type="button" title="Remover" aria-label="Remover" onClick={onRemove}
          className="p-1.5 rounded text-red-500 hover:bg-red-50">
          <span className="material-symbols-outlined text-[18px]">delete</span>
        </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Tipo">
          <select className="input" value={q.type} onChange={(e) => setType(e.target.value)}>
            {QUESTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>
        <div className="flex items-end pb-2">
          <Toggle checked={q.required} onChange={(v) => set({ required: v })} label="Obrigatória" />
        </div>
      </div>

      {isChoice(q.type) && (
        <Field label={`Opções (${options.length}/${MAX_OPTIONS})`}>
          <div className="space-y-2">
            {options.map((opt, i) => (
              <div key={i} className="flex gap-2">
                <input
                  className="input" value={opt} maxLength={200} aria-label={`Opção ${i + 1}`}
                  onChange={(e) => set({ options: options.map((o, j) => (j === i ? e.target.value : o)) })}
                />
                <button type="button" title="Remover opção" aria-label="Remover opção"
                  disabled={options.length <= 1}
                  onClick={() => set({ options: options.filter((_, j) => j !== i) })}
                  className="p-1.5 rounded text-red-500 hover:bg-red-50 disabled:opacity-30">
                  <span className="material-symbols-outlined text-[18px]">close</span>
                </button>
              </div>
            ))}
            <button type="button" className="text-sm text-primary font-bold disabled:opacity-40"
              disabled={options.length >= MAX_OPTIONS}
              onClick={() => set({ options: [...options, `Opção ${options.length + 1}`] })}>
              + Adicionar opção
            </button>
          </div>
        </Field>
      )}

      {linked && (
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Mapear para (CRM)">
            <select className="input" value={mapsToSelectValue(q.mapsTo)}
              onChange={(e) => {
                const v = e.target.value;
                if (v === 'custom') set({ mapsTo: 'custom:' });
                else if (!v) { const { mapsTo, ...rest } = q; onChange(rest); } else set({ mapsTo: v });
              }}>
              {STANDARD_MAPS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </Field>
          {mapsToSelectValue(q.mapsTo) === 'custom' && (
            <Field label="Chave do campo" hint="Letras, números, _ e -">
              <input className="input" value={q.mapsTo.slice(7)} maxLength={60}
                onChange={(e) => set({ mapsTo: `custom:${e.target.value.replace(/[^a-zA-Z0-9_-]/g, '')}` })} />
            </Field>
          )}
        </div>
      )}
    </div>
  );
}

export default function QuestionsTab({ schema, onChange, linked }) {
  const questions = schema.questions || [];
  const setQuestions = (next) => onChange({ ...schema, questions: next });

  const add = () => {
    if (questions.length >= MAX_QUESTIONS) return;
    setQuestions([...questions, { id: nextQuestionId(questions), type: 'short_text', label: '', required: false }]);
  };
  const move = (i, dir) => {
    const next = [...questions];
    const [item] = next.splice(i, 1);
    next.splice(i + dir, 0, item);
    setQuestions(next);
  };

  return (
    <div className="space-y-4">
      {questions.length === 0 && <p className="text-sm text-slate-500">Nenhuma pergunta. Adicione a primeira.</p>}
      {questions.map((q, i) => (
        <QuestionCard
          key={q.id} q={q} index={i} total={questions.length} linked={linked}
          onChange={(nq) => setQuestions(questions.map((x, j) => (j === i ? nq : x)))}
          onMove={(dir) => move(i, dir)}
          onRemove={() => setQuestions(questions.filter((_, j) => j !== i))}
        />
      ))}
      <button type="button" className="btn btn-secondary flex items-center gap-2" disabled={questions.length >= MAX_QUESTIONS} onClick={add}>
        <span className="material-symbols-outlined text-[18px]">add</span>
        Adicionar pergunta ({questions.length}/{MAX_QUESTIONS})
      </button>
    </div>
  );
}
